import * as React from "react";
import { Formik, Form } from "formik";
import { Card } from "@mui/material";
import { withStyles } from "@mui/styles";
import InputElement from "../../Common/InputElement";
import TeamHistory from "../../Common/Widgets/TeamHistory";
import { cloneDeep } from "lodash";

const useStyles = (theme) => ({
    "@global": {
        body: {
            backgroundColor: theme.palette.common.white,
        },
    },
    paper: {
        marginTop: theme.spacing(4),
        padding: "16px",
    },
});

class UserDetailsForm extends React.Component {
    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            fieldListData: null,
            teamList: [],
            openCvr: false,
            cvrData: [],
            savingField: null
        };
    }

    componentDidMount() {
        this.dataFormation();
    }

    componentDidUpdate(prevProps) {
        // Clear the per-field loading state once the save request that triggered
        // it has finished (isLoading transitions back to false), whether it
        // succeeded or failed.
        if (this.state.savingField && prevProps.isLoading && !this.props.isLoading) {
            this.setState({ savingField: null });
        }

        // Rebuild the dropdown option lists (incl. the Team list feeding this form
        // and the Team History widget) whenever the underlying data refreshes — e.g.
        // after a soft-deleted team is dropped from the teams list, or the profile is
        // re-fetched to revert a rejected team switch. dataFormation otherwise only
        // runs once on mount, leaving a stale (still-selectable) team dropdown.
        const userDetailsChanged = prevProps.userDetails !== this.props.userDetails;

        if (
            prevProps.teams !== this.props.teams ||
            userDetailsChanged ||
            prevProps.countries !== this.props.countries ||
            prevProps.userRoles !== this.props.userRoles ||
            prevProps.salesReps !== this.props.salesReps
        ) {
            this.dataFormation();
        }

        // Re-sync the Team field with the saved profile whenever the profile is
        // re-fetched. Formik's enableReinitialize does NOT do this when a rejected
        // team switch leaves the profile deep-equal to before (the save never
        // happened) — so the field would otherwise keep the failed (now removed)
        // selection and render blank. Set it explicitly so it reverts to the
        // user's previously saved team (and reflects the new team on a successful
        // switch).
        if (userDetailsChanged && this.formRef.current && this.props.userDetails) {
            this.formRef.current.setFieldValue('team_id', this.props.userDetails.team_id);
        }
    }

    dataFormation = () => {
        const { countries, userRoles, teams, salesReps } = this.props;
        let objCountry = {};
        let objRoles = {};
        let objTeams = {};

        let list = [];
        let rolesList = [];
        let teamList = [];
        let usersList = [];

        countries && countries.main.map((item, _key) => {
            return list.push({ id: item.country_id, name: item.country_name });
        });

        userRoles && userRoles.map((item, _key) => {
            return rolesList.push({ id: item.role_id, name: item.role_title });
        });

        teams && teams.rows.map((item, _key) => {
            // Soft-deleted teams must never be selectable — not in the main Team
            // field, nor in the team-history transition dropdowns that share this
            // list. Keep them visible but disabled (marked "(Deleted)") so existing
            // references still resolve to a name without being re-selectable.
            return teamList.push({
                id: item.team_id,
                name: item.deleted_at ? `${item.team_name} (Deleted)` : item.team_name,
                disabled: !!item.deleted_at,
            });
        });

        // If the user is still linked to a soft-deleted team, keep showing it as a
        // read-only current value: it is not among the selectable (active) options,
        // so surface it as a disabled entry rather than letting the field go blank.
        const { userDetails } = this.props;
        if (userDetails && userDetails.team_id && !teamList.some((t) => t.id === userDetails.team_id)) {
            teamList.push({
                id: userDetails.team_id,
                name: userDetails.team_name
                    ? `${userDetails.team_name}${userDetails.team_deleted ? ' (Deleted)' : ''}`
                    : 'Deleted team',
                disabled: true,
            });
        }

        salesReps && salesReps.main.map((item, _key) => {
            return usersList.push({ id: item.user_id, name: item.user_email });
        });

        objCountry.list = list;
        objRoles.list = rolesList;
        objTeams.list = teamList;

        const fieldList = [
            {
                title: "First Name",
                name: "user_firstname",
                type: "textfield",
            },
            {
                title: "Last Name",
                name: "user_lastname",
                type: "textfield",
            },
            {
                title: "Display Name",
                name: "display_name",
                type: "textfield",
            },
            {
                title: "Email",
                name: "user_email",
                type: "textfield",
            },
            {
                title: "Title",
                name: "user_title",
                type: "textfield",
            },
            {
                title: "Role",
                name: "role_id",
                type: "dropdown",
                list: objRoles.list,
            },
            {
                title: "Phone",
                name: "user_phone",
                type: "textfield",
            },
            {
                title: "LinkedIn",
                name: "user_linkedin",
                type: "textfield",
            },
            {
                title: "Team",
                name: "team_id",
                type: "dropdown",
                list: objTeams.list,
            },
            {
                title: "Country",
                name: "country_id",
                type: "dropdown",
                list: objCountry.list,
            },
            {
                title: "Alias",
                name: "alias_account_id",
                type: "dropdown",
                list: usersList,
            },
            {
                title: 'Deactivate',
                name: 'user_deactivate',
                type: 'checkbox'
            },
            {
                title: 'Is Parent Account?',
                name: 'is_parent',
                type: 'checkbox'
            },
            {
                title:'Economic Id',
                name:'user_economic_id',
                type:'textfield'
            }
        ];

        this.setState({ fieldListData: fieldList, teamList: teamList });
    };

    handleSaveClick = (_e, name) => {
        const {onClick} = this.props;
        let formData = this.formRef.current.values;
        this.setState({ savingField: name });
        onClick({ [name]: formData[name] });
    };


    render() {
        const { classes, userDetails, userTeamHistory, onAddTransition, onEditTransition } = this.props;
        const { fieldListData, teamList, savingField } = this.state;
        var initialData = cloneDeep(userDetails);

        // Handle checkboxes
        initialData.user_deactivate = !!initialData.user_deactivated
        initialData.is_parent = !!initialData.is_parent

        return (
            <React.Fragment>
                {fieldListData && (
                    <Card className={classes.paper}>
                        <Formik
                            innerRef={this.formRef}
                            enableReinitialize
                            initialValues={initialData}
                            validate={(_values) => {
                                const errors = {};
                                return errors;
                            }}
                            onSubmit={(values, { setSubmitting }) => {
                                setTimeout(() => {
                                    setSubmitting(false);
                                    this.props.saveNews(values);
                                }, 500);
                            }}
                            render={({ _submitForm, _isSubmitting, _values, _setFieldValue }) => (
                                <Form>
                                    {fieldListData.map((item, index) => {
                                        if (item.name === "team_id") {
                                            return (
                                                <React.Fragment key={index}>
                                                    <InputElement
                                                        {...item}
                                                        saving={savingField === item.name}
                                                        onSaveClick={this.handleSaveClick}
                                                    />
                                                    <TeamHistory
                                                        teamHistory={userTeamHistory}
                                                        teamsList={teamList}
                                                        userId={userDetails.user_id}
                                                        onAddTransition={onAddTransition}
                                                        onEditTransition={onEditTransition}
                                                    />
                                                </React.Fragment>
                                            );
                                        }
                                        return (
                                            <InputElement
                                                {...item}
                                                key={index}
                                                saving={savingField === item.name}
                                                onSaveClick={this.handleSaveClick}
                                            />
                                        );
                                    })}
                                </Form>
                            )}
                        />
                    </Card>
                )}

            </React.Fragment>
        );
    }
}

export default withStyles(useStyles)(UserDetailsForm);